import React, { useRef } from 'react';
import { BattleUnitState, BattleTileState, TurnOrderEntry, HotbarSkill, StatusEffect, AttackForecast } from '../types/battle';
import { getTileColor, getTileAtPosition, getUnitAtPosition, getHealthColor } from '../utils/battleUtils';
import { getUnitSpriteLayers, getTerrainSprite } from '../utils/spriteMap';
import { UnitReactionState } from '../hooks/useBattleEffects';
import BattleUnitEquipment from './BattleUnitEquipment';

// Long-press tuning for the touch tile/unit inspector (GH-63): held past this with no
// movement opens the inspector; the shared touch-pan-zoom recognizer already suppresses
// the ghost click for any touch held past its own 300ms tap threshold, so a successful
// long-press never also fires the tile's normal onClick action.
const LONG_PRESS_MS = 500;
const LONG_PRESS_MOVE_CANCEL_PX = 10;

interface BattleGridTilesProps {
  gridWidth: number;
  gridHeight: number;
  tiles: BattleTileState[];
  units: BattleUnitState[];
  selectedUnit: BattleUnitState | null;
  currentTurnUnit: TurnOrderEntry | null;
  validMoves: { x: number; y: number }[];
  validTargets: BattleUnitState[];
  skillTargets: { x: number; y: number }[];
  skillRange: { x: number; y: number }[];
  aoeArea: { x: number; y: number }[];
  weaponRange: { x: number; y: number }[];
  attackMode: boolean;
  skillMode: boolean;
  selectedSkill: HotbarSkill | null;
  itemTargets: BattleUnitState[];
  unitReactions: Record<number, UnitReactionState>;
  dyingUnits: Record<number, BattleUnitState>;
  pendingConfirmTile: { x: number; y: number } | null;
  attackForecast: AttackForecast[] | null;
  threatTiles: { x: number; y: number }[];
  onTileClick: (x: number, y: number) => void;
  onTileHover: (x: number, y: number) => void;
  onTileLeave: () => void;
  onTileInspect: (x: number, y: number) => void;
}

const getForecastTooltip = (f: AttackForecast): string => {
  let tooltip = `${f.hitChance}% to hit\n${f.minDamage}-${f.maxDamage} damage`;
  if (f.critChance > 0) tooltip += `\n${f.critChance}% crit (${f.minCritDamage}-${f.maxCritDamage} dmg)`;
  if (f.blockChance > 0) tooltip += `\n${f.blockChance}% chance to block (halves damage)`;
  return tooltip;
};

export const getEffectIcon = (effectType: string): string => {
  switch (effectType) {
    case 'Poison':          return '🧪';
    case 'Burn':            return '🔥';
    case 'Invisibility':    return '👻';
    case 'Root':            return '🌿';
    case 'DamageBuff':      return '⚔️';
    case 'DamageReduction': return '🛡️';
    case 'StatBuff':        return '✨';
    case 'Stun':            return '💫';
    default:                return '❓';
  }
};

export const getTileTooltip = (tile: BattleTileState | undefined, x: number, y: number): string => {
  if (!tile) return `(${x},${y})`;
  let tooltip = `${tile.type} (${x},${y})`;
  if (tile.movementCost > 1)  tooltip += `\nMovement Cost: ${tile.movementCost}`;
  if (tile.evasionBonus > 0)  tooltip += `\n+${tile.evasionBonus}% Evasion`;
  else if (tile.evasionBonus < 0) tooltip += `\n${tile.evasionBonus}% Evasion`;
  if (tile.defenseBonus > 0)  tooltip += `\n+${tile.defenseBonus}% Defense`;
  if (tile.hazardType)        tooltip += `\n⚠️ ${tile.hazardType} Hazard (${tile.hazardDamage} dmg/turn)`;
  if (!tile.isPassable)       tooltip += `\nImpassable`;
  if (tile.blocksLineOfSight) tooltip += `\nBlocks Vision`;
  return tooltip;
};

const BattleGridTiles: React.FC<BattleGridTilesProps> = ({
  gridWidth, gridHeight, tiles, units,
  selectedUnit, currentTurnUnit,
  validMoves, validTargets, skillTargets, skillRange, aoeArea, weaponRange,
  attackMode, skillMode, selectedSkill,
  itemTargets,
  unitReactions, dyingUnits,
  pendingConfirmTile,
  attackForecast,
  threatTiles,
  onTileClick, onTileHover, onTileLeave, onTileInspect,
}) => {
  // Shared across all tiles since only one touch gesture is ever in flight at a time.
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressStartRef = useRef<{ x: number; y: number } | null>(null);

  const clearLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  return (
    <>
      {Array.from({ length: gridHeight }, (_, y) => (
        <div key={y} className="grid-row">
          {Array.from({ length: gridWidth }, (_, x) => {
            const tile = getTileAtPosition(tiles, x, y);
            const unit = getUnitAtPosition(units, x, y);
            const isSelected = !!(selectedUnit && unit && selectedUnit.id === unit.id);
            const isValidMove = validMoves.some(m => m.x === x && m.y === y);
            const isValidTarget = !!(unit && validTargets.some(t => t.id === unit.id));
            const isSkillTarget = skillTargets.some(t => t.x === x && t.y === y);
            const isItemTarget = !!(unit && itemTargets.some(t => t.id === unit.id));
            const isSkillRangeTile = skillRange.some(r => r.x === x && r.y === y);
            const isAoeArea = aoeArea.some(a => a.x === x && a.y === y);
            const isCurrentTurnUnit = !!(unit && currentTurnUnit && unit.id === currentTurnUnit.unitId);
            const isWeaponRange = weaponRange.some(r => r.x === x && r.y === y);
            const isPendingConfirm = !!(pendingConfirmTile && pendingConfirmTile.x === x && pendingConfirmTile.y === y);
            const isThreatTile = threatTiles.some(t => t.x === x && t.y === y);

            const tileClass = [
              'grid-tile',
              isSelected ? 'selected' : '',
              unit ? 'has-unit' : '',
              isCurrentTurnUnit ? 'current-turn-unit' : '',
              isValidMove ? 'valid-move' : '',
              isValidTarget ? 'valid-target' : '',
              isSkillTarget ? 'skill-target' : '',
              isItemTarget ? 'item-target' : '',
              isSkillRangeTile ? 'skill-range' : '',
              isAoeArea ? 'aoe-area' : '',
              isWeaponRange ? 'weapon-range' : '',
              tile?.hazardType ? 'hazard-tile' : '',
              isPendingConfirm ? 'pending-confirm' : '',
              isThreatTile ? 'threat-tile' : '',
            ].filter(Boolean).join(' ');

            const terrainSprite = tile ? getTerrainSprite(tile) : null;
            const baseColor    = tile ? getTileColor(tile) : '#333';

            const handleTouchStart = (e: React.TouchEvent) => {
              if (e.touches.length !== 1) { clearLongPress(); return; }
              const t = e.touches[0];
              longPressStartRef.current = { x: t.clientX, y: t.clientY };
              clearLongPress();
              longPressTimerRef.current = setTimeout(() => {
                longPressTimerRef.current = null;
                onTileInspect(x, y);
              }, LONG_PRESS_MS);
            };
            const handleTouchMove = (e: React.TouchEvent) => {
              const start = longPressStartRef.current;
              if (!start || e.touches.length !== 1) { clearLongPress(); return; }
              const t = e.touches[0];
              if (Math.hypot(t.clientX - start.x, t.clientY - start.y) > LONG_PRESS_MOVE_CANCEL_PX) clearLongPress();
            };

            return (
              <div
                key={`${x}-${y}`}
                className={tileClass}
                style={{
                  backgroundColor: baseColor,
                  backgroundImage: terrainSprite ? `url(${terrainSprite})` : undefined,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  imageRendering: 'pixelated',
                }}
                onClick={() => onTileClick(x, y)}
                onMouseEnter={() => onTileHover(x, y)}
                onMouseLeave={onTileLeave}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={clearLongPress}
                onTouchCancel={clearLongPress}
                title={getTileTooltip(tile, x, y)}
              >
                <div className="tile-coords">{x},{y}</div>
                {isPendingConfirm && (
                  <div className="tile-confirm-badge" title="Tap again to confirm">✓</div>
                )}
                {tile?.hazardType && (
                  <div className="hazard-indicator" title={`${tile.hazardType} Hazard: ${tile.hazardDamage} damage/turn`}>
                    ⚠️
                  </div>
                )}
                {unit && (() => {
                  const spriteLayers = getUnitSpriteLayers(unit);
                  const hpPct     = unit.stats.maxHP > 0
                    ? Math.max(0, unit.stats.currentHP / unit.stats.maxHP)
                    : 0;
                  const hpColor   = getHealthColor(hpPct);
                  // hitKey/lungeKey bump on every trigger; folding them into the sprite-wrap's
                  // `key` force-remounts it so the CSS animation restarts cleanly even when hits
                  // land back-to-back on the same unit (AOE, combos) - no class-toggle timing.
                  const reaction = unitReactions[unit.id];
                  const spriteAnimClass =
                    (reaction?.hitKey ? ' unit-sprite--hit' : '') +
                    (reaction?.lungeKey ? ' unit-sprite--lunge' : '');
                  // AI-turn deaths keep the unit in battle.units for the rest of the action
                  // sequence (removal only lands once the whole turn's final state is applied),
                  // so a live unit can itself be "dying" - unlike the player-attack path, where
                  // the unit vanishes from state immediately and the ghost block below takes over.
                  const isDying = !!dyingUnits[unit.id];
                  const difficulty = unit.monster?.difficulty;
                  const difficultyClass = difficulty && difficulty !== 'Normal' ? ` unit--${difficulty.toLowerCase()}` : '';
                  const difficultyBadge = difficulty === 'Elite' ? '⭐' : difficulty === 'Boss' ? '👑' : difficulty === 'Legendary' ? '💀' : null;
                  const forecast = attackForecast?.find(f => f.unitId === unit.id);

                  return (
                    <div className={`unit ${unit.isPlayerUnit ? 'player-unit' : 'enemy-unit'}${isDying ? ' unit--dying' : ''}${difficultyClass}`}>
                      {/* Backdrop - makes units readable against busy terrain */}
                      <div className="unit-backdrop" />

                      {/* Attack forecast (GH-66): hit%/damage range/crit% while targeting */}
                      {forecast && (
                        <div className="attack-forecast-badge" title={getForecastTooltip(forecast)}>
                          <span
                            className="forecast-hit"
                            style={{ color: forecast.hitChance >= 70 ? '#7fd97f' : forecast.hitChance >= 40 ? '#e6b84a' : '#ff6b6b' }}
                          >
                            {forecast.hitChance}%
                          </span>
                          <span className="forecast-dmg">{forecast.minDamage}-{forecast.maxDamage}</span>
                          {forecast.critChance > 0 && (
                            <span className="forecast-crit">✨{forecast.critChance}%</span>
                          )}
                        </div>
                      )}

                      {/* Sprite (stacked layers: base body -> beard -> hair for players).
                          Each layer gets its own flex-centering wrap so layers overlay
                          exactly rather than laying out side-by-side. */}
                      {spriteLayers.map((layer, idx) => (
                        <div
                          className="unit-sprite-wrap"
                          key={`${layer.url}-${idx}-${reaction?.hitKey ?? 0}-${reaction?.lungeKey ?? 0}`}
                        >
                          <img
                            src={layer.url}
                            alt={unit.name}
                            className={`unit-sprite${unit.isPlayerUnit ? '' : ' unit-sprite--enemy'}${spriteAnimClass}`}
                            style={layer.filter ? ({ '--skin-tint': layer.filter } as React.CSSProperties) : undefined}
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                            draggable={false}
                          />
                        </div>
                      ))}

                      {/* Name caption */}
                      <div className="unit-name">{difficultyBadge && `${difficultyBadge} `}{unit.name}</div>

                      {/* HP bar */}
                      <div className="unit-hp-bar">
                        <div
                          className="unit-hp-fill"
                          style={{ width: `${hpPct * 100}%`, backgroundColor: hpColor }}
                        />
                      </div>

                      {/* Status effect icons */}
                      {unit.statusEffects && unit.statusEffects.length > 0 && (
                        <div className="unit-status-icons">
                          {unit.statusEffects.slice(0, 3).map((effect: StatusEffect, idx: number) => (
                            <span key={idx} className="status-icon" title={`${effect.effectName} (${effect.turnsRemaining} turns)`}>
                              {getEffectIcon(effect.effectType)}
                            </span>
                          ))}
                          {unit.statusEffects.length > 3 && (
                            <span className="status-icon-more" title={`+${unit.statusEffects.length - 3} more effects`}>
                              +{unit.statusEffects.length - 3}
                            </span>
                          )}
                        </div>
                      )}

                      <BattleUnitEquipment equipment={unit.equipment || {}} isPlayerUnit={unit.isPlayerUnit} />
                    </div>
                  );
                })()}
                {/* Death ghost (GH-55): the server deletes the real unit as soon as defeat is
                    applied, so by the time setBattle reflects that, `unit` above is already gone.
                    Render a short-lived snapshot from dyingUnits so the fall/fade has something
                    to animate rather than the unit just vanishing instantly. */}
                {!unit && (() => {
                  const ghost = Object.values(dyingUnits).find(
                    (u) => u.position.x === x && u.position.y === y
                  );
                  if (!ghost) return null;
                  const ghostSpriteLayers = getUnitSpriteLayers(ghost);
                  return (
                    <div className={`unit ${ghost.isPlayerUnit ? 'player-unit' : 'enemy-unit'} unit--dying`}>
                      <div className="unit-backdrop" />
                      {ghostSpriteLayers.map((layer, idx) => (
                        <div className="unit-sprite-wrap" key={`ghost-${layer.url}-${idx}`}>
                          <img
                            src={layer.url}
                            alt={ghost.name}
                            className={`unit-sprite${ghost.isPlayerUnit ? '' : ' unit-sprite--enemy'}`}
                            style={layer.filter ? ({ '--skin-tint': layer.filter } as React.CSSProperties) : undefined}
                            draggable={false}
                          />
                        </div>
                      ))}
                      <div className="unit-name">{ghost.name}</div>
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      ))}
    </>
  );
};

export default BattleGridTiles;
